import React from 'react'
import { useQuery, gql } from '@apollo/client'
import { Button } from '@/atoms/Button'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionLG, TitleLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'

export const SMALL_INVESTMENT_BANNER_INFO = {
  sketch: {
    name: 'Sketch',
    bannerBackgroundImg: '/v1752177734/angel-studios/banners/sketch-banner-bg',
    focusBackgroundImg: '/v1752177776/angel-studios/banners/sketch-foreground',
    ctaPath: paths.external.invest.sketch,
  },
} as const

const GET_PROJECT_LOGO = gql`
  query getProjectLogo($slug: String!) {
    project(slug: $slug) {
      logoCloudinaryPath
    }
  }
`

interface SmallInvestmentBannerProps {
  slug: keyof typeof SMALL_INVESTMENT_BANNER_INFO
}

export const SmallInvestmentBanner: React.FC<SmallInvestmentBannerProps> = ({ slug }) => {
  const { t } = useTranslate('common')
  const { innerWidth } = useWindowSize()
  const bannerInfo = SMALL_INVESTMENT_BANNER_INFO[slug]
  const { data } = useQuery(GET_PROJECT_LOGO, { variables: { slug } })
  const logoPath = data?.project?.logoCloudinaryPath

  if (!bannerInfo) {
    return null
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-white/30">
      <ResponsiveBackgroundImage
        responsiveAR={{
          xs: 3200 / 304,
          sm: 3200 / 304,
          md: 3200 / 304,
          lg: 3200 / 304,
          xl: 3200 / 304,
          '2xl': 3200 / 304,
          '3xl': 3200 / 304,
        }}
        backgroundPosition={innerWidth > 768 ? 'bg-center' : 'bg-right'}
        gravity={innerWidth > 768 ? 'g_center' : 'g_west'}
        src={bannerInfo.bannerBackgroundImg}
      />
      <ResponsiveBackgroundImage
        src={bannerInfo.focusBackgroundImg}
        responsiveAR={{
          xs: 2,
          sm: 2,
          md: 2,
          lg: 2,
          xl: 2,
          '2xl': 2,
          '3xl': 2,
        }}
        backgroundSize={innerWidth < 390 ? 'bg-cover' : 'bg-contain'}
        backgroundPosition={innerWidth > 767 ? 'bg-center' : 'bg-right'}
        className="translate-x-[15%] sm:translate-x-0 xs:translate-x-0"
      />
      <div className="relative flex items-center justify-between p-5 md:mx-auto md:max-w-[862px] lg:max-w-[942px]">
        <div className="hidden text-center md:block">
          <TitleLG weight="semibold" className="mb-3">
            {t('investToday', 'Invest Today!')}
          </TitleLG>
          {logoPath && <Image src={logoPath} alt={bannerInfo.name} width={212} height={212} />}
        </div>
        <div className="max-w-[212px] md:text-center">
          <TitleMD weight="bold" className="mb-2 md:hidden">
            {t('investToday', 'Invest Today!')}
          </TitleMD>
          <CaptionLG color="white" className="mb-5">
            {t('supportProjectTheatricalRelease', 'Support {{project}} theatrical release now.', {
              project: bannerInfo.name,
            })}
          </CaptionLG>
          <BeforeYouLeaveProvider>
            <BeforeYouLeaveModal />
            <AngelFundingLink
              linkContext="Project Journey"
              href={bannerInfo.ctaPath}
              leavingContinueLabel={t('continue', 'Continue')}
              leavingMessage={t(
                'youAreNowLeavingThisWebsiteToEnterAFINRARegulatedPortal',
                'You are now leaving this website to enter a FINRA regulated portal.',
              )}
            >
              <Button size="sm" variant="offwhite" className="inline-flex items-center gap-1">
                {t('learnMore', 'Learn More')}
                <ExternalLinkIcon color="white" size={20} />
              </Button>
            </AngelFundingLink>
          </BeforeYouLeaveProvider>
        </div>
      </div>
    </div>
  )
}
